import React, { useState, useEffect } from "react";
import { database, storage } from "../../firebaseConfig";
import { ref, set, push, onValue, get } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import OfferServiceItem from "./OfferServiceItem.js";
import OfferPreviewPage from "./OfferPreviewPage";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./OfferMaker.css";

// ServiceSelectionModal Component
const ServiceSelectionModal = ({ 
  isOpen, 
  onClose, 
  services, 
  selectedServiceIds,
  onSelectService, 
  searchTerm, 
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  categories
}) => {
  if (!isOpen) return null;

  return (
    <div className="offer-maker__modal-overlay">
      <div className="offer-maker__modal">
        <div className="offer-maker__modal-header">
          <h3 className="offer-maker__modal-title">
            <span className="offer-maker__modal-icon">🛠️</span>
            Add Services to Offer
          </h3>
          <button 
            className="offer-maker__modal-close" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="offer-maker__modal-filters">
          <div className="offer-maker__search-container">
            <input 
              type="text" 
              placeholder="Search services..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="offer-maker__search-input"
            />
            {searchTerm && (
              <button 
                className="offer-maker__clear-search" 
                onClick={() => setSearchTerm("")}
              >
                ×
              </button>
            )}
          </div>
          
          <div className="offer-maker__category-filter">
            <select 
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="offer-maker__category-select"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="offer-maker__modal-content">
          {services.length === 0 ? (
            <div className="offer-maker__empty-state">
              <p>No services found matching your filter criteria.</p>
            </div>
          ) : (
            <div className="offer-maker__services-grid">
              {services.map(service => (
                <div 
                  key={service.id} 
                  className={`offer-maker__service-selection-item ${
                    selectedServiceIds.includes(service.id) ? 'offer-maker__service-selection-item--selected' : ''
                  }`}
                  onClick={() => onSelectService(service.id)}
                >
                  <OfferServiceItem 
                    service={service} 
                    isCompact={true} 
                    onEdit={() => {}} 
                    onDelete={() => {}}
                  />
                  <div className="offer-maker__service-selection-checkbox">
                    {selectedServiceIds.includes(service.id) ? (
                      <span className="offer-maker__checkbox-checked">✓</span>
                    ) : (
                      <span className="offer-maker__checkbox-unchecked"></span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="offer-maker__modal-footer">
          <button 
            className="offer-maker__modal-cancel" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="offer-maker__modal-save" 
            onClick={onClose}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component OfferMaker
const OfferMaker = ({ userId, initialOfferData = null, isEditMode = false, onOfferSaved = () => {} }) => {
  const [services, setServices] = useState([]);
  const [currentOffer, setCurrentOffer] = useState({
    title: "",
    description: "",
    services: [],
    notes: "",
    logo: "",
    logoMaxHeight: 80, // Standard-Höhe
    createdAt: Date.now(),
    offerValidTill: "",
    estimatedTimespan: "",
    isRecurring: false,
    customerName: "",
    globalDiscount: 0,
    showPaymentButton: false,
    paymentButtonText: "Pay with Stripe",
    paymentButtonLink: "",
    teamMember: null,
    language: "en",
    includeTax: false,
    taxRate: 20,
    reverseCharge: false
  });
  const [offerId, setOfferId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for the service selection modal
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [autoSaveId, setAutoSaveId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [lastSavedOffer, setLastSavedOffer] = useState(null);

  // Load services from the database
  useEffect(() => {
    if (!userId) return;

    const servicesRef = ref(database, `/World/${userId}/services`);
    
    const unsubscribe = onValue(servicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const servicesData = snapshot.val();
        const servicesArray = Object.entries(servicesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setServices(servicesArray);
        
        // Extract categories
        const uniqueCategories = [...new Set(servicesArray
          .map(service => service.category)
          .filter(category => category && category.trim() !== "")
        )];
        setCategories(uniqueCategories);
      } else {
        setServices([]);
        setCategories([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Initialize the offer with provided data, if available
  useEffect(() => {
    if (initialOfferData) {
      if (isEditMode && initialOfferData.id) {
        setOfferId(initialOfferData.id);
      } else {
        setOfferId(null);
      }
      
      // Stelle sicher, dass die Logo-Höhe nicht über 400px liegt
      const logoHeight = initialOfferData.logoMaxHeight ? 
        Math.min(400, Math.round(initialOfferData.logoMaxHeight / 5) * 5) : 
        80;

      setCurrentOffer({
        title: initialOfferData.title || "",
        description: initialOfferData.description || "",
        services: initialOfferData.services || [],
        notes: initialOfferData.notes || "",
        logo: initialOfferData.logo || "",
        logoMaxHeight: logoHeight,
        createdAt: initialOfferData.createdAt || Date.now(),
        offerValidTill: initialOfferData.offerValidTill || "",
        estimatedTimespan: initialOfferData.estimatedTimespan || "",
        isRecurring: initialOfferData.isRecurring || false,
        customerName: initialOfferData.customerName || "",
        globalDiscount: initialOfferData.globalDiscount || 0,
        showPaymentButton: initialOfferData.showPaymentButton || false,
        paymentButtonText: initialOfferData.paymentButtonText || "Pay with Stripe",
        paymentButtonLink: initialOfferData.paymentButtonLink || "",
        teamMember: initialOfferData.teamMember || null,
        language: initialOfferData.language || "en",
        includeTax: initialOfferData.includeTax || false,
        taxRate: initialOfferData.taxRate || 20,
        reverseCharge: initialOfferData.reverseCharge || false
      });
      
      if (initialOfferData.logo) {
        setLogoUrl(initialOfferData.logo);
      }

      if (initialOfferData.services && initialOfferData.services.length === 0) {
        setTimeout(() => {
          setIsModalOpen(true);
        }, 500);
      }
    }
  }, [initialOfferData, isEditMode]);

  // Load team members
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!userId) return;

      try {
        const teamRef = ref(database, `/World/${userId}/team/members`);
        const snapshot = await get(teamRef);

        if (snapshot.exists()) {
          const members = [];
          snapshot.forEach((childSnapshot) => {
            members.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setTeamMembers(members);

          // If editing and there's a team member ID, select it
          if (isEditMode && initialOfferData?.teamMemberId) {
            const member = members.find(m => m.id === initialOfferData.teamMemberId);
            if (member) {
              setSelectedTeamMember(member);
            }
          }
        }
      } catch (error) {
        console.error("Error loading team members:", error);
      }
    };

    loadTeamMembers();
  }, [userId, isEditMode, initialOfferData]);

  // Auto-Save Funktion
  const autoSaveOffer = async (offerToSave) => {
    if (!userId || !offerToSave.services || offerToSave.services.length === 0) return;
    
    // Prüfe, ob tatsächlich Änderungen vorliegen
    if (lastSavedOffer && 
        JSON.stringify(offerToSave) === JSON.stringify(lastSavedOffer)) {
      return; // Keine Änderungen, kein Speichern nötig
    }
    
    // Wenn wir im Edit-Modus sind und eine offerId haben, nutzen wir diese
    if (isEditMode && offerId) {
      try {
        // Berechne die Preise
        const offerServices = services.filter(
          service => offerToSave.services.includes(service.id)
        );
          
        const originalTotal = offerServices.reduce((sum, service) => sum + service.price, 0);
        const servicesDiscountedTotal = offerServices.reduce((sum, service) => {
          const discountedPrice = service.price * (1 - (service.discountPercent / 100));
          return sum + discountedPrice;
        }, 0);
        
        const globalDiscountMultiplier = 1 - (offerToSave.globalDiscount / 100);
        const discountedTotal = servicesDiscountedTotal * globalDiscountMultiplier;
        
        // Apply tax if enabled (but not for reverse charge)
        const taxAmount = (offerToSave.includeTax && !offerToSave.reverseCharge) ? discountedTotal * (offerToSave.taxRate / 100) : 0;
        const finalTotal = discountedTotal + taxAmount;

        // Erstelle das komplette Offer-Objekt
        const updatedOffer = {
          ...offerToSave,
          originalTotal,
          servicesDiscountedTotal,
          discountedTotal,
          taxAmount,
          finalTotal,
          teamMember: selectedTeamMember ? {
            name: selectedTeamMember.name,
            companyName: selectedTeamMember.companyName,
            companySlogan: selectedTeamMember.companySlogan,
            personalEmail: selectedTeamMember.personalEmail,
            generalEmail: selectedTeamMember.generalEmail,
            phone: selectedTeamMember.phone,
            website: selectedTeamMember.website,
            bankName: selectedTeamMember.bankName,
            iban: selectedTeamMember.iban,
            bic: selectedTeamMember.bic,
            signature: selectedTeamMember.signature || null,
            signatureMaxWidth: selectedTeamMember.signatureMaxWidth || 150
          } : null,
          updatedAt: Date.now()
        };

        const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
        await set(offerRef, updatedOffer);
        setLastAutoSave(new Date());
        setLastSavedOffer(offerToSave);
        // Keine Benachrichtigung mehr
      } catch (error) {
        console.error("Auto-Save Error:", error);
        // Keine Benachrichtigung bei Fehlern
      }
    }
  };

  // Auto-Save Effect
  useEffect(() => {
    if (isEditMode && offerId && currentOffer.services.length > 0) {
      const timeoutId = setTimeout(() => {
        autoSaveOffer(currentOffer);
      }, 30000); // Warte 30 Sekunden nach der letzten Änderung

      return () => clearTimeout(timeoutId);
    }
  }, [currentOffer, services, selectedTeamMember, isEditMode, offerId]);

  // Speichere die initiale Angebotsversion für die Änderungserkennung
  useEffect(() => {
    if (initialOfferData && isEditMode) {
      setLastSavedOffer(initialOfferData);
    }
  }, [initialOfferData, isEditMode]);

  // Modifiziere handleToggleService um den autoSaveId zu berücksichtigen
  const handleToggleService = (serviceId) => {
    setCurrentOffer(prevOffer => {
      const updatedOffer = {
        ...prevOffer,
        services: prevOffer.services.includes(serviceId)
          ? prevOffer.services.filter(id => id !== serviceId)
          : [...prevOffer.services, serviceId]
      };

      // Wenn es der erste Service ist, setze einen Titel falls keiner existiert
      if (updatedOffer.services.length === 1 && !updatedOffer.title) {
        const service = services.find(s => s.id === serviceId);
        if (service) {
          updatedOffer.title = `Angebot: ${service.title}`;
        }
      }

      return updatedOffer;
    });
  };

  // Upload logo
  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if it's an image
    if (!file.type.match('image.*')) {
      showNotification("Please select an image file (jpg, png, etc.)", "error");
      return;
    }
    
    // Check size limit (max. 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showNotification("The image file is too large (max. a2MB)", "error");
      return;
    }
    
    setLogoFile(file);
    
    // Display preview image
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoUrl(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoUrl("");
    setCurrentOffer(prevOffer => ({
      ...prevOffer,
      logo: ""
    }));
  };

  // Handle team member selection
  const handleTeamMemberChange = (e) => {
    const memberId = e.target.value;
    const member = teamMembers.find(m => m.id === memberId);
    setSelectedTeamMember(member || null);
  };

  // Modifiziere handleSaveOffer um den autoSaveId zu berücksichtigen
  const handleSaveOffer = async () => {
    if (!userId) return;
    
    if (!currentOffer.services || currentOffer.services.length === 0) {
      showNotification("Please add at least one service", "error");
      return;
    }

    if (!currentOffer.title) {
      showNotification("Please enter a title", "error");
      return;
    }

    // Upload logo, if available
    let finalLogoUrl = currentOffer.logo;
    if (logoFile) {
      setIsUploading(true);
      
      try {
        const timestamp = new Date().getTime();
        const fileExtension = logoFile.name.split('.').pop();
        const fileName = `offer_logos/${timestamp}_${fileExtension}`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, logoFile);
        finalLogoUrl = await getDownloadURL(fileRef);
        
        setIsUploading(false);
      } catch (error) {
        console.error("Error uploading logo:", error);
        showNotification("The logo could not be uploaded. Please try again.", "error");
        setIsUploading(false);
        return;
      }
    }

    // Calculate price information
    const offerServices = services.filter(
      service => currentOffer.services.includes(service.id)
    );
      
    const originalTotal = offerServices.reduce((sum, service) => sum + service.price, 0);
    const servicesDiscountedTotal = offerServices.reduce((sum, service) => {
      const discountedPrice = service.price * (1 - (service.discountPercent / 100));
      return sum + discountedPrice;
    }, 0);
    
    // Apply global discount
    const globalDiscountMultiplier = 1 - (currentOffer.globalDiscount / 100);
    const discountedTotal = servicesDiscountedTotal * globalDiscountMultiplier;
    
    // Apply tax if enabled (but not for reverse charge)
    const taxAmount = (currentOffer.includeTax && !currentOffer.reverseCharge) ? discountedTotal * (currentOffer.taxRate / 100) : 0;
    const finalTotal = discountedTotal + taxAmount;

    // Complete offer object
    const updatedOffer = {
      ...currentOffer,
      logo: finalLogoUrl,
      originalTotal,
      servicesDiscountedTotal,
      discountedTotal,
      taxAmount,
      finalTotal,
      teamMemberId: selectedTeamMember?.id || null,
      teamMember: selectedTeamMember ? {
        name: selectedTeamMember.name,
        companyName: selectedTeamMember.companyName,
        companySlogan: selectedTeamMember.companySlogan,
        personalEmail: selectedTeamMember.personalEmail,
        generalEmail: selectedTeamMember.generalEmail,
        phone: selectedTeamMember.phone,
        website: selectedTeamMember.website,
        bankName: selectedTeamMember.bankName,
        iban: selectedTeamMember.iban,
        bic: selectedTeamMember.bic,
        signature: selectedTeamMember.signature || null,
        signatureMaxWidth: selectedTeamMember.signatureMaxWidth || 150
      } : null,
      updatedAt: Date.now()
    };

    try {
      // Determine the reference path based on the mode (Edit/New)
      let offerRef;
      if (isEditMode && offerId) {
        // When editing, update the existing record
        offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
        
        // Remove ID from the object before saving
        const { id, ...offerWithoutId } = updatedOffer;
        
        await set(offerRef, offerWithoutId);
        showNotification("Offer successfully updated", "success");
      } else {
        // When creating, create a new record
        offerRef = ref(database, `/World/${userId}/offers`);
        const newOfferRef = push(offerRef);
        
        // For a new offer, set the creation date
        const newOffer = {
          ...updatedOffer,
          createdAt: Date.now(),
        };
        
        await set(newOfferRef, newOffer);
        showNotification("Offer successfully saved", "success");
      }

      // Reset form
      setCurrentOffer({
        title: "",
        description: "",
        services: [],
        notes: "",
        logo: "",
        logoMaxHeight: 80,
        createdAt: Date.now(),
        offerValidTill: "",
        estimatedTimespan: "",
        isRecurring: false,
        customerName: "",
        globalDiscount: 0,
        showPaymentButton: false,
        paymentButtonText: "Pay with Stripe",
        paymentButtonLink: "",
        teamMember: null,
        language: "en",
        includeTax: false,
        taxRate: 20,
        reverseCharge: false
      });
      setLogoFile(null);
      setLogoUrl("");
      setOfferId(null);

      // Execute callback when the offer has been saved
      onOfferSaved();
    } catch (error) {
      console.error("Error saving offer:", error);
      showNotification("Error saving offer", "error");
    }

    setAutoSaveId(null); // Reset autoSaveId nach manuellem Speichern
  };

  // Show and hide notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    
    // Automatically hide after 3 seconds
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // Filter services
  const getFilteredServices = () => {
    return services.filter(service => {
      const matchesSearch = searchTerm.trim() === "" || 
        service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === "" || service.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  };

  // Available services (for modal)
  const availableServices = getFilteredServices();

  // Services in the offer
  const offerServices = services.filter(
    service => currentOffer.services.includes(service.id)
  );

  // Open modal
  const openServiceModal = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setIsModalOpen(true);
  };

  // Update offer information
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentOffer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link'
  ];

  return (
    <div className="offer-maker">
      {notification.message && (
        <div className={`offer-maker__notification offer-maker__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="offer-maker__header">
        <h2 className="offer-maker__title">
          {isEditMode ? "Edit Offer" : "Create Offer"}
        </h2>
      </div>
      
      <div className="offer-maker__content">
        {/* Left column: Editor */}
        <div className="offer-maker__editor">
          <div className="offer-maker__section">
            <div className="offer-maker__section-header">
              <span className="offer-maker__section-icon">📝</span>
              <h3 className="offer-maker__section-title">Offer Information</h3>
            </div>

            <div className="offer-maker__language-toggle">
              <label className="offer-maker__language-label">Offer Language</label>
              <div className="offer-maker__toggle-container">
                <label className="offer-maker__toggle-switch">
                  <input
                    type="checkbox"
                    className="offer-maker__toggle-input"
                    checked={currentOffer.language === 'de'}
                    onChange={(e) => setCurrentOffer(prev => ({
                      ...prev,
                      language: e.target.checked ? 'de' : 'en'
                    }))}
                  />
                  <span className="offer-maker__toggle-slider"></span>
                </label>
                <span className="offer-maker__toggle-label">
                  {currentOffer.language === 'de' ? '🇩🇪 Deutsch' : '🇺🇸 English'}
                </span>
              </div>
            </div>
            
            <div className="offer-maker__form-group">
              <label className="offer-maker__label">Offer Title</label>
              <input
                type="text"
                className="offer-maker__input"
                value={currentOffer.title || ''}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter offer title"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Customer Name</label>
              <input
                type="text"
                name="customerName"
                value={currentOffer.customerName}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="Customer name"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Description</label>
              <ReactQuill
                value={currentOffer.description}
                onChange={(value) => setCurrentOffer(prev => ({ ...prev, description: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Describe your offer..."
                className="offer-maker__quill-editor"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Valid until</label>
              <input
                type="date"
                name="offerValidTill"
                value={currentOffer.offerValidTill}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="MM/DD/YYYY"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Estimated delivery time</label>
              <input
                type="text"
                name="estimatedTimespan"
                value={currentOffer.estimatedTimespan}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="e.g. 2-3 weeks or 10 business days"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Global Discount (%)</label>
              <input
                type="number"
                name="globalDiscount"
                value={currentOffer.globalDiscount}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="e.g. 20 for 20% discount"
                min="0"
                max="100"
              />
            </div>

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.includeTax}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    includeTax: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Include taxes in price
              </span>
            </div>

            {currentOffer.includeTax && (
              <div className="offer-maker__input-group">
                <label className="offer-maker__label">Tax Rate (%)</label>
                <input
                  type="number"
                  name="taxRate"
                  value={currentOffer.taxRate}
                  onChange={handleInputChange}
                  className="offer-maker__input"
                  placeholder="e.g. 19 for 19% VAT"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            )}

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.reverseCharge}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    reverseCharge: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Reverse Charge
              </span>
            </div>



            <div className="offer-maker__section-divider"></div>

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.showPaymentButton}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    showPaymentButton: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Show payment button
              </span>
            </div>

            {currentOffer.showPaymentButton && (
              <>
                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">Button Text</label>
                  <input
                    type="text"
                    name="paymentButtonText"
                    value={currentOffer.paymentButtonText}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder="e.g. Pay with Stripe"
                  />
                </div>

                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">Payment Link</label>
                  <input
                    type="url"
                    name="paymentButtonLink"
                    value={currentOffer.paymentButtonLink}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder="https://stripe.com/pay/..."
                  />
                  <small className="offer-maker__input-hint">
                    Der Link wird in einem neuen Tab geöffnet
                  </small>
                </div>
              </>
            )}

            <div className="offer-maker__section-divider"></div>

            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Additional Notes</label>
              <ReactQuill
                value={currentOffer.notes}
                onChange={(value) => setCurrentOffer(prev => ({ ...prev, notes: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Additional information such as payment terms, delivery time, etc."
                className="offer-maker__quill-editor"
              />
            </div>
            
            <div className="offer-maker__team-select-container">
              <label className="offer-maker__team-select-label">Team Member</label>
              <select
                className="offer-maker__team-select"
                value={selectedTeamMember?.id || ""}
                onChange={handleTeamMemberChange}
              >
                <option value="">Select team member</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name} - {member.companyName}
                  </option>
                ))}
              </select>
              
              {selectedTeamMember && (
                <div className="offer-maker__team-member-card">
                  <div className="offer-maker__team-member-info">
                    <div className="offer-maker__team-member-avatar">
                      {selectedTeamMember.name.charAt(0)}
                    </div>
                    <div className="offer-maker__team-member-details">
                      <div className="offer-maker__team-member-name">
                        {selectedTeamMember.name}
                      </div>
                      <div className="offer-maker__team-member-company">
                        {selectedTeamMember.companyName}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="offer-maker__section">
            <div className="offer-maker__section-header">
              <span className="offer-maker__section-icon">🖼️</span>
              <h3 className="offer-maker__section-title">Customer Logo</h3>
            </div>
            
            <div className="offer-maker__logo-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="offer-maker__logo-input"
                disabled={isUploading}
                id="logo-upload"
              />
              
              <div className="offer-maker__logo-settings">
                <label className="offer-maker__logo-height-label">
                  Logo Height: {currentOffer.logoMaxHeight}px
                  <div className="offer-maker__logo-height-controls">
                    <input
                      type="range"
                      min="10"
                      max="400"
                      step="5"
                      value={currentOffer.logoMaxHeight}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        const percentage = ((value - 10) / (400 - 10)) * 100;
                        e.target.style.setProperty('--slider-value', `${percentage}%`);
                        setCurrentOffer(prev => ({
                          ...prev,
                          logoMaxHeight: value
                        }));
                      }}
                      className="offer-maker__logo-height-slider"
                      style={{ '--slider-value': `${((currentOffer.logoMaxHeight - 10) / (400 - 10)) * 100}%` }}
                    />
                    <input
                      type="number"
                      min="10"
                      max="400"
                      step="5"
                      value={currentOffer.logoMaxHeight}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value >= 10 && value <= 1000) {
                          const roundedValue = Math.round(value / 5) * 5;
                          const percentage = ((roundedValue - 10) / (1000 - 10)) * 100;
                          const slider = e.target.previousSibling;
                          slider.style.setProperty('--slider-value', `${percentage}%`);
                          setCurrentOffer(prev => ({
                            ...prev,
                            logoMaxHeight: roundedValue
                          }));
                        }
                      }}
                      className="offer-maker__logo-height-input"
                    />
                  </div>
                </label>
              </div>
              
              {logoUrl ? (
                <div className="offer-maker__logo-preview">
                  <img 
                    src={logoUrl} 
                    alt="Customer logo preview" 
                    style={{ maxHeight: `${currentOffer.logoMaxHeight}px` }}
                  />
                  <button
                    type="button"
                    className="offer-maker__logo-remove"
                    onClick={handleRemoveLogo}
                    title="Remove logo"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label htmlFor="logo-upload" className="offer-maker__logo-placeholder">
                  <span className="offer-maker__logo-icon">📷</span>
                  <span className="offer-maker__logo-text">
                    Click here to upload a customer logo
                  </span>
                </label>
              )}
            </div>
          </div>
          
          <div className="offer-maker__section">
            <div className="offer-maker__section-header">
              <span className="offer-maker__section-icon">🛠️</span>
              <h3 className="offer-maker__section-title">Services</h3>
            </div>
            
            <button 
              className="offer-maker__services-btn" 
              onClick={openServiceModal}
            >
              <span className="offer-maker__services-btn-icon">+</span>
              Add services to offer
            </button>
            
            <div className="offer-maker__selected-services">
              {offerServices.length === 0 ? (
                <div className="offer-maker__no-services">
                  <p>No services selected yet</p>
                </div>
              ) : (
                <ul className="offer-maker__services-list">
                  {offerServices.map(service => (
                    <li key={service.id} className="offer-maker__service-item">
                      <div className="offer-maker__service-info">
                        <span className="offer-maker__service-title">{service.title}</span>
                        <span className="offer-maker__service-price">
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                            service.price * (1 - service.discountPercent / 100)
                          )}
                        </span>
                      </div>
                      <button 
                        className="offer-maker__service-remove" 
                        onClick={() => handleToggleService(service.id)}
                        title="Remove from offer"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          
          <button 
            className="offer-maker__save-btn" 
            onClick={handleSaveOffer}
            disabled={isUploading || currentOffer.services.length === 0 || !currentOffer.title}
          >
            {isUploading ? "Saving..." : isEditMode ? "Save changes" : "Save offer"}
            <span className="offer-maker__save-icon">💾</span>
          </button>
        </div>
        
        {/* Right column - Preview */}
        <div className="offer-maker__preview-container">
          <div className="offer-maker__preview-header">
            <span className="offer-maker__preview-icon">👁️</span>
            <h3 className="offer-maker__preview-title">Preview</h3>
          </div>
          
          <div className="offer-maker__preview-wrapper">
            <OfferPreviewPage 
              offerData={{
                ...currentOffer,
                logo: logoUrl || currentOffer.logo,
                originalTotal: offerServices.reduce((sum, service) => sum + service.price, 0),
                servicesDiscountedTotal: offerServices.reduce((sum, service) => sum + (service.price * (1 - service.discountPercent / 100)), 0),
                discountedTotal: offerServices.reduce((sum, service) => sum + (service.price * (1 - service.discountPercent / 100)), 0) * (1 - (currentOffer.globalDiscount / 100)),
                taxAmount: (currentOffer.includeTax && !currentOffer.reverseCharge) ? 
                  (offerServices.reduce((sum, service) => sum + (service.price * (1 - service.discountPercent / 100)), 0) * (1 - (currentOffer.globalDiscount / 100))) * (currentOffer.taxRate / 100) : 0,
                finalTotal: (currentOffer.includeTax && !currentOffer.reverseCharge) ? 
                  (offerServices.reduce((sum, service) => sum + (service.price * (1 - service.discountPercent / 100)), 0) * (1 - (currentOffer.globalDiscount / 100))) * (1 + (currentOffer.taxRate / 100)) :
                  offerServices.reduce((sum, service) => sum + (service.price * (1 - service.discountPercent / 100)), 0) * (1 - (currentOffer.globalDiscount / 100)),
                teamMember: selectedTeamMember
              }}
              services={offerServices}
              onlyPreview={true}
            />
          </div>
        </div>
      </div>
      
      {/* Modal for service selection */}
      <ServiceSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        services={availableServices}
        selectedServiceIds={currentOffer.services}
        onSelectService={handleToggleService}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        categories={categories}
      />
    </div>
  );
};

export default OfferMaker; 